import type { PersonRow, DayRow } from './people-types';

/** Colours used for time everywhere: Akhara terracotta, Mitti verdant. */
export const AKHARA_BG = 'bg-terracotta-500';
export const MITTI_BG = 'bg-verdant-500 dark:bg-verdant-300';
export const AKHARA_TEXT = 'text-terracotta-600 dark:text-terracotta-300';
export const MITTI_TEXT = 'text-verdant-600 dark:text-verdant-300';

export interface DayDoc {
  date?: string;
  akharaMin?: number;
  mittiMin?: number;
  steps?: number;
  zor?: number;
  akharaMinPrior?: number;
  mittiMinPrior?: number;
  stepsPrior?: number;
  zorPrior?: number;
}

const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Live plus prior (backfilled) values. The only way days are read. */
export function dayTotals(d: DayDoc | null | undefined): {
  akharaMin: number;
  mittiMin: number;
  steps: number;
  zor: number;
  hasPrior: boolean;
} {
  const x = d ?? {};
  return {
    akharaMin: n(x.akharaMin) + n(x.akharaMinPrior),
    mittiMin: n(x.mittiMin) + n(x.mittiMinPrior),
    steps: n(x.steps) + n(x.stepsPrior),
    zor: n(x.zor) + n(x.zorPrior),
    hasPrior: n(x.akharaMinPrior) > 0,
  };
}

export function sumDays(docs: DayDoc[]): { akharaMin: number; mittiMin: number; steps: number; zor: number } {
  const out = { akharaMin: 0, mittiMin: 0, steps: 0, zor: 0 };
  for (const d of docs) {
    const t = dayTotals(d);
    out.akharaMin += t.akharaMin;
    out.mittiMin += t.mittiMin;
    out.steps += t.steps;
    out.zor += t.zor;
  }
  return out;
}

/** IST (UTC+5:30, no DST) date as YYYY-MM-DD. */
export function istDate(ms: number): string {
  return new Date(ms + 5.5 * 3600_000).toISOString().slice(0, 10);
}

/** First day of "this week": the last 7 IST days including today. */
export function weekStartIst(ms: number): string {
  return istDate(ms - 6 * 86_400_000);
}

// ---- roster helpers ----

export type TypeFilter = 'all' | 'team' | 'candidate';
export type SortKey = 'name' | 'stage' | 'certs' | 'week' | 'allTime' | 'zor' | 'lastActive' | 'mitti';
export type SortDir = 'asc' | 'desc';

export const SORT_KEYS: SortKey[] = ['name', 'stage', 'certs', 'week', 'allTime', 'zor', 'lastActive', 'mitti'];

/** Mirrors PATH_ORDER in functions/src/people-time.ts. */
export const PATH_ORDER = [
  'track_welcome',
  'track_aspirant_role',
  'track_aspirant_problem',
  'track_aspirant_readiness',
  'track_cert_foundations',
  'track_cert_configurator',
  'track_cert_architecture',
  'track_cert_irm',
] as const;

/** The direction a column sorts in on first click: numbers and time most first; name, stage, Mitti ascending. */
export function naturalDir(key: SortKey): SortDir {
  return key === 'name' || key === 'stage' || key === 'mitti' ? 'asc' : 'desc';
}

export function isSortKey(v: unknown): v is SortKey {
  return typeof v === 'string' && (SORT_KEYS as string[]).includes(v);
}

const weekTotal = (p: PersonRow) => p.week.akharaMin + p.week.mittiMin;
const allTotal = (p: PersonRow) => p.allTime.akharaMin + p.allTime.mittiMin;

export function filterPeople(people: PersonRow[], type: TypeFilter, search: string): PersonRow[] {
  const q = search.trim().toLowerCase();
  return people.filter((p) => {
    if (type !== 'all' && p.type !== type) return false;
    if (!q) return true;
    return p.displayName.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
  });
}

// Every stored state, most usable first; anything unknown sorts after these.
const MITTI_RANK: Record<string, number> = { running: 0, starting: 1, ready: 2, provisioning: 3, stopped: 4, failed: 5, expired: 6 };

/** The value a key sorts on; null means empty, which sorts last in both directions. */
function sortValue(p: PersonRow, key: SortKey): number | string | null {
  const bad = p.incomplete === true;
  switch (key) {
    case 'name':
      return (p.displayName || p.email).toLowerCase();
    case 'stage': {
      if (bad || !p.stage) return null;
      const i = (PATH_ORDER as readonly string[]).indexOf(p.stage.trackId);
      const frac = p.stage.total > 0 ? Math.min(1, p.stage.done / p.stage.total) : 0;
      return (i < 0 ? PATH_ORDER.length : i) * 10 + frac * 9;
    }
    case 'certs':
      return bad ? null : Number(p.certs.foundations) + Number(p.certs.configurator) + Number(p.certs.architecture === true) + Number(p.certs.irm);
    case 'week':
      return bad ? null : weekTotal(p);
    case 'allTime':
      return bad ? null : allTotal(p);
    case 'zor':
      return p.allTime.zor;
    case 'lastActive': {
      const ms = p.lastActiveAt ? new Date(p.lastActiveAt).getTime() : NaN;
      return Number.isFinite(ms) ? ms : null;
    }
    case 'mitti':
      return p.mitti ? (MITTI_RANK[p.mitti.state] ?? 7) : null;
  }
}

export function sortPeople(people: PersonRow[], key: SortKey, dir: SortDir): PersonRow[] {
  const byName = (a: PersonRow, b: PersonRow) =>
    (a.displayName || a.email).localeCompare(b.displayName || b.email);
  const sign = dir === 'asc' ? 1 : -1;
  return [...people].sort((a, b) => {
    const x = sortValue(a, key);
    const y = sortValue(b, key);
    if (x === null && y !== null) return 1;
    if (y === null && x !== null) return -1;
    if (x !== null && y !== null && x !== y) {
      const c = typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y) : (x as number) - (y as number);
      if (c !== 0) return sign * c;
    }
    return byName(a, b);
  });
}

export function typeCounts(people: PersonRow[]): { all: number; team: number; candidate: number } {
  const team = people.filter((p) => p.type === 'team').length;
  return { all: people.length, team, candidate: people.length - team };
}

export function rosterTiles(people: PersonRow[], now: number) {
  let active = 0;
  let akharaMin = 0;
  let mittiMin = 0;
  let steps = 0;
  let quiet = 0;
  for (const p of people) {
    const wk = weekTotal(p);
    if (wk > 0) active += 1;
    akharaMin += p.week.akharaMin;
    mittiMin += p.week.mittiMin;
    steps += p.week.steps;
    const last = p.lastActiveAt ? new Date(p.lastActiveAt).getTime() : NaN;
    const quietFor7 = !Number.isFinite(last) || now - last > 7 * 86_400_000;
    if (p.stage && !p.paused && wk === 0 && p.week.steps === 0 && quietFor7) quiet += 1;
  }
  return { active, total: people.length, akharaMin, mittiMin, steps, quiet };
}

export function relativeAgo(iso: string | null, now: number): string {
  if (!iso) return '—';
  const diff = now - new Date(iso).getTime();
  if (!Number.isFinite(diff)) return '—';
  const abs = Math.max(0, diff);
  if (abs < 60_000) return 'just now';
  if (abs < 3_600_000) return `${Math.round(abs / 60_000)} min ago`;
  if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)} h ago`;
  return `${Math.round(abs / 86_400_000)} d ago`;
}

// ---- person chart ----

export interface WeekBar {
  /** Monday-agnostic: the first date of the 7-day block. */
  start: string;
  end: string;
  akharaMin: number;
  mittiMin: number;
  hasPrior: boolean;
}

/** Groups the oldest-first day list into 7-day blocks ending today (last block = this week). */
export function weeklyBars(days: DayRow[]): WeekBar[] {
  const bars: WeekBar[] = [];
  const blocks = Math.ceil(days.length / 7);
  for (let b = 0; b < blocks; b++) {
    const end = days.length - (blocks - 1 - b) * 7;
    const slice = days.slice(Math.max(0, end - 7), end);
    if (slice.length === 0) continue;
    bars.push({
      start: slice[0].date,
      end: slice[slice.length - 1].date,
      akharaMin: slice.reduce((s, d) => s + d.akharaMin, 0),
      mittiMin: slice.reduce((s, d) => s + d.mittiMin, 0),
      hasPrior: slice.some((d) => d.prior),
    });
  }
  return bars;
}

/** A round chart maximum in minutes (1, 2, 4, 5, 10, 20, 40, 50 ... hours) that fits `minutes`; at least 1 h. */
export function niceMax(minutes: number): number {
  const hours = Math.max(1, minutes / 60);
  const mag = Math.pow(10, Math.floor(Math.log10(hours)));
  for (const m of [1, 2, 4, 5, 10]) if (m * mag >= hours) return m * mag * 60;
  return 10 * mag * 60;
}
